import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Logo from "../components/Logo";
import Footer from "../components/Footer";
import waiversService from "../api/services/waivers.service";

const Waivers = () => {
  const navigate = useNavigate();
  const [pendingWaivers, setPendingWaivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // One typed signature covers every outstanding document. Families asked
  // to tick a box per form abandon the page; the club asked for a single
  // act of signing, which is also what /waivers/accept-all records.
  const [signature, setSignature] = useState("");
  const [agreedToAll, setAgreedToAll] = useState(false);
  const [expanded, setExpanded] = useState({});

  // Fetch pending waivers on mount
  useEffect(() => {
    fetchPendingWaivers();
  }, []);

  const fetchPendingWaivers = async () => {
    try {
      setLoading(true);
      // include_enrolled brings in the waivers scoped to this family's own
      // programs — the cancellation policies. Without it the page answers
      // with the globally-scoped forms only, so a parent sent here to sign
      // "our updated policies" would have signed three of the six.
      const response = await waiversService.getPending({
        include_enrolled: true,
      });

      // Backend returns: { items: [{waiver_template, is_accepted, acceptance, needs_reconsent}], pending_count, total }
      // Extract waiver_template from each item
      const waiversData = (response?.items || []).map(item => ({
        id: item.waiver_template.id,
        name: item.waiver_template.name,
        content: item.waiver_template.content,
        type: item.waiver_template.waiver_type,
        version: item.waiver_template.version,
        needs_reconsent: item.needs_reconsent,
        is_accepted: item.is_accepted,
      }));

      setPendingWaivers(waiversData);
    } catch (error) {
      console.error("Failed to fetch pending waivers:", error);
      toast.error("Failed to load waivers");
      setPendingWaivers([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const toggleDocument = (waiverId) => {
    setExpanded((prev) => ({ ...prev, [waiverId]: !prev[waiverId] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreedToAll) {
      toast.error("Please tick the box to agree to the documents");
      return;
    }

    if (!signature.trim()) {
      toast.error("Please type your full name as your signature");
      return;
    }

    try {
      setSubmitting(true);

      // One request, one timestamp across every outstanding document. The
      // old client-side loop could half-sign a family if a request dropped.
      // Must resolve the same set the page displayed. Without this the
      // server signs only the globally-scoped waivers, so a parent shown
      // six documents signs three and is told all six are done.
      const result = await waiversService.acceptAll({
        signer_name: signature.trim(),
        include_enrolled: true,
      });

      toast.success(
        result?.signed_count === 1
          ? "Your document is signed."
          : `All ${result?.signed_count || pendingWaivers.length} documents are signed.`,
      );

      // Navigate to dashboard
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (error) {
      console.error("Failed to sign waivers:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Failed to sign waivers. Please try again.";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col justify-center items-center overflow-y-auto relative py-8 sm:py-0">

      {/* Dotted Background */}
      <div className="absolute inset-0 bg-[radial-gradient(#a1acc7_1px,transparent_1px)] [background-size:18px_18px] opacity-70"></div>

      {/* CSF School Academy - Top Center */}
      <div className="absolute top-8 left-1/2 -translate-x-1/2 z-10">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[62px] font-bold text-[#173151] font-kollektif drop-shadow-lg">CSF School Academy</h1>
      </div>

      <div className='relative justify-center items-center w-full max-w-md sm:max-w-2xl md:max-w-4xl px-4 sm:px-6 mt-20 sm:mt-24 md:mt-32'>
        {/* WAIVER CARD */}
        <div className="bg-white shadow-2xl rounded-2xl sm:rounded-3xl px-4 sm:px-6 md:px-8 py-4 sm:py-6 w-full max-h-[calc(100vh-120px)] sm:max-h-[calc(100vh-180px)] md:max-h-[calc(100vh-200px)] overflow-y-auto">

          {loading && (
            <div className="text-center py-10">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#173151] border-r-transparent"></div>
              <p className="mt-4 text-gray-600">Loading waivers...</p>
            </div>
          )}

          {!loading && (
            <>
          {/* Logo and Title Section */}
          <div className="relative flex items-center mb-6">
            {/* Logo - Left Side */}
            <div className="flex w-16 h-16 sm:w-24 sm:h-24 md:w-[128px] md:h-[124px] items-center justify-center">
              <Logo />
            </div>

            {/* Title - Centered */}
            <div className="absolute left-1/2 -translate-x-1/2 text-center">
              <h2 className="text-2xl font-semibold text-[#0f172a]">Registration Documents</h2>
              <p className="text-gray-500 mt-1">
                {pendingWaivers.length > 0
                  ? "One signature covers all of them"
                  : "Review and sign required documents"}
              </p>
            </div>
          </div>

        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Dynamic Waiver Sections from API */}
          {pendingWaivers.length === 0 ? (
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200 text-center">
              <p className="text-blue-800 font-medium">No pending waivers found</p>
              <p className="text-blue-600 text-sm mt-2">All required waivers have been signed</p>
            </div>
          ) : (
            pendingWaivers.map((waiver) => (
              <div key={waiver.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-[#173151]">
                      {waiver.name || waiver.type}
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Version {waiver.version}
                      {waiver.needs_reconsent && waiver.is_accepted
                        ? " · updated since you last signed"
                        : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleDocument(waiver.id)}
                    className="shrink-0 text-sm font-semibold text-[#173151] underline"
                  >
                    {expanded[waiver.id] ? "Hide" : "Read"}
                  </button>
                </div>
                {expanded[waiver.id] && (
                  <div className="text-gray-700 text-sm mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap border-t border-gray-200 pt-3">
                    {waiver.content || "Please review and accept this waiver."}
                  </div>
                )}
              </div>
            ))
          )}

          {/* One agreement, one signature, all documents */}
          {pendingWaivers.length > 0 && (
            <div className="mt-2 rounded-xl border border-[#173151]/20 bg-[#f6f8fb] p-4">
              <label className="flex items-start gap-3 text-gray-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToAll}
                  onChange={(e) => setAgreedToAll(e.target.checked)}
                  className="mt-0.5 w-5 h-5 text-[#173151] border-gray-300 rounded focus:ring-[#173151]"
                />
                <span className="font-medium text-sm sm:text-base">
                  I have read and agree to{" "}
                  {pendingWaivers.length === 1
                    ? "the document above"
                    : `all ${pendingWaivers.length} documents above`}
                  , on behalf of my child or children.
                </span>
              </label>

              <div className="mt-4">
                <label
                  htmlFor="waiver-signature"
                  className="font-medium text-gray-700 mb-2 block text-sm"
                >
                  Signature
                </label>
                <input
                  id="waiver-signature"
                  type="text"
                  name="signature"
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  placeholder="Type your full name"
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#173151] bg-white outline-none"
                  required
                />
                <p className="text-xs text-gray-500 mt-2">
                  Typing your name signs every document listed above. We record
                  the date and time, and you can download a copy of each one at
                  any time.
                </p>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-3 sm:gap-4 justify-end mt-6">
            <button
              type="button"
              onClick={() => window.history.back()}
              className="bg-gray-200 text-gray-700 px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base rounded-xl font-semibold shadow-md hover:bg-gray-300 transition"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-[#173151] text-white px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base rounded-xl font-semibold shadow-md hover:bg-[#1f3d67] transition disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={submitting}
            >
              {submitting
                ? "Signing..."
                : pendingWaivers.length > 1
                  ? `Sign all ${pendingWaivers.length} documents`
                  : "Sign document"}
            </button>
          </div>

        </form>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Waivers;
